import authService from './authService';
import { ApiResponse, PaginatedResponse } from '../types/common';
import {
  InvoiceShippingAssignmentPayload,
  InvoiceShippingOrderCandidate,
  InvoiceShippingOrderSearchModel,
  InvoiceShippingOrderSendPayload,
  InvoiceShippingOrderSendResult,
} from '../types/invoice';

class InvoiceShippingOrderService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async extractErrorMessage(response: Response, fallback: string): Promise<string> {
    const body = await response.json().catch(() => null) as
      | { message?: string; error?: string | { message?: string; description?: string }; details?: unknown }
      | null;

    if (!body) return fallback;
    if (typeof body.message === 'string' && body.message.trim()) return body.message;

    if (typeof body.error === 'string' && body.error.trim()) return body.error;

    if (body.error && typeof body.error === 'object') {
      if (typeof body.error.message === 'string' && body.error.message.trim()) return body.error.message;
      if (typeof body.error.description === 'string' && body.error.description.trim()) return body.error.description;
    }

    if (body.details && typeof body.details === 'object') {
      try {
        const values = Object.values(body.details as Record<string, unknown>)
          .map((v) => (typeof v === 'string' ? v.trim() : ''))
          .filter(Boolean);
        if (values.length > 0) return values[0];
      } catch {
        // Ignore malformed details payload and fallback.
      }
    }

    return fallback;
  }

  async getInvoices(searchModel: InvoiceShippingOrderSearchModel): Promise<PaginatedResponse<InvoiceShippingOrderCandidate>> {
    const params = new URLSearchParams({
      date: searchModel.date,
      page: String(searchModel.page),
      page_size: String(searchModel.pageSize),
      ...(searchModel.search ? { search: searchModel.search } : {}),
      ...(searchModel.company_id !== undefined ? { company_id: String(searchModel.company_id) } : {}),
      ...(searchModel.provider_id !== undefined ? { provider_id: String(searchModel.provider_id) } : {}),
      ...(searchModel.integration_status ? { integration_status: searchModel.integration_status } : {}),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoices/shipping-orders?${params}`);
    if (!response.ok) {
      const fallback = 'Erro ao buscar pedidos de envio';
      throw new Error(await this.extractErrorMessage(response, fallback));
    }

    const json = await response.json();
    const payload = json.data ?? json;
    const items: InvoiceShippingOrderCandidate[] = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
      ? payload
      : [];
    const pagination = payload?.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pagination.page ?? searchModel.page,
        limit: pagination.page_size ?? pagination.limit ?? searchModel.pageSize,
        total: pagination.total_items ?? pagination.total ?? items.length,
        totalPages:
          pagination.total_pages ??
          pagination.totalPages ??
          Math.max(1, Math.ceil((pagination.total_items ?? pagination.total ?? items.length) / searchModel.pageSize)),
      },
    };
  }

  async updateAssignment(
    invoiceId: number,
    payload: InvoiceShippingAssignmentPayload,
  ): Promise<InvoiceShippingOrderCandidate> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/invoices/${invoiceId}/shipping-order-assignment`, payload);
    if (!response.ok) {
      if (response.status === 404) {
        const fallback = 'Nota fiscal não encontrada';
        throw new Error(await this.extractErrorMessage(response, fallback));
      }
      if (response.status === 409) {
        const fallback = 'Esta nota fiscal já foi integrada com a transportadora';
        throw new Error(await this.extractErrorMessage(response, fallback));
      }
      if (response.status === 422) {
        const fallback = 'Não foi possível salvar a seleção por inconsistência operacional';
        throw new Error(await this.extractErrorMessage(response, fallback));
      }
      const fallback = 'Erro ao salvar seleção de transportadora';
      throw new Error(await this.extractErrorMessage(response, fallback));
    }
    const data: ApiResponse<InvoiceShippingOrderCandidate> = await response.json();
    return data.data!;
  }

  async send(invoiceId: number, payload: InvoiceShippingOrderSendPayload): Promise<InvoiceShippingOrderSendResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/invoices/${invoiceId}/shipping-order`, payload);
    if (!response.ok) {
      if (response.status === 404) {
        const fallback = 'Nota fiscal, transportadora ou serviço não encontrado';
        throw new Error(await this.extractErrorMessage(response, fallback));
      }
      if (response.status === 409) {
        const fallback = 'Esta nota fiscal já foi integrada com a transportadora';
        throw new Error(await this.extractErrorMessage(response, fallback));
      }
      if (response.status === 422) {
        const fallback = 'Não foi possível enviar a nota por inconsistência operacional';
        throw new Error(await this.extractErrorMessage(response, fallback));
      }
      const fallback = 'Erro ao enviar nota fiscal para a transportadora';
      throw new Error(await this.extractErrorMessage(response, fallback));
    }
    const data: ApiResponse<InvoiceShippingOrderSendResult> = await response.json();
    return data.data!;
  }

  /** Baixa o PDF único com os rótulos dos Correios (um por volume) da nota integrada. */
  async downloadCorreiosLabels(invoiceId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/invoices/${invoiceId}/correios-labels`);
    if (!response.ok) {
      const fallback = 'Erro ao baixar as etiquetas dos Correios';
      throw new Error(await this.extractErrorMessage(response, fallback));
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `etiquetas-correios-nf-${invoiceId}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  /** Cancela as pré-postagens Correios da nota e libera a NF para nova integração. */
  async cancelShippingOrder(invoiceId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/invoices/${invoiceId}/shipping-order/cancel`, {});
    if (!response.ok) {
      if (response.status === 409) {
        const fallback = 'O pedido de envio não pode mais ser cancelado (objeto já postado ou em trânsito)';
        throw new Error(await this.extractErrorMessage(response, fallback));
      }
      const fallback = 'Erro ao cancelar o pedido de envio nos Correios';
      throw new Error(await this.extractErrorMessage(response, fallback));
    }
  }
}

export const invoiceShippingOrderService = new InvoiceShippingOrderService();
export default invoiceShippingOrderService;